import type { InsidedEmbedConfig } from './embed-widget.types'

export function getEmbedGlobal(): InsidedEmbedConfig {
  if (!window.__insidedEmbed) {
    window.__insidedEmbed = { communityUrl: '' }
  }
  return window.__insidedEmbed
}

function loadScript(script: HTMLScriptElement, target: HTMLElement = document.body): Promise<void> {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script')

    for (const attr of script.attributes) {
      newScript.setAttribute(attr.name, attr.value)
    }

    if (script.src) {
      newScript.onload = () => resolve()
      newScript.onerror = () => reject(new Error(`Failed to load script: ${script.src}`))
    } else {
      newScript.textContent = script.textContent
    }

    target.appendChild(newScript)

    if (!script.src) {
      resolve()
    }
  })
}

function isStylesheetLink(link: HTMLLinkElement): boolean {
  const rel = (link.getAttribute('rel') ?? '').trim().toLowerCase()
  const tokens = rel ? rel.split(/\s+/) : []
  return tokens.includes('stylesheet')
}

/** Resolve href to an absolute URL; returns null if empty or invalid. */
function resolveStylesheetUrl(href: string | null): string | null {
  if (href == null) return null
  const trimmed = href.trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed).href
  } catch {
    console.error(`Invalid stylesheet URL: ${href}`)
    return null
  }
}

async function loadWidgetResources(communityUrl: string): Promise<void> {
  const params = new URLSearchParams()
  const productType = getEmbedGlobal().productType
  if (productType) params.set('productType', productType)

  const qs = params.toString()
  const url = qs ? `${communityUrl}/widget/resources?${qs}` : `${communityUrl}/widget/resources`

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget resources: ${response.status}`)
  }

  const html = await response.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const elements = Array.from(doc.head.children)

  const csrfInput = doc.getElementById('csrftoken') as HTMLInputElement | null
  if (csrfInput?.value) {
    getEmbedGlobal().csrfToken = csrfInput.value
  }

  for (const el of elements) {
    await processHeadElement(el)
  }
}

function getHeadResources() {
  const global = getEmbedGlobal()
  if (!global.headResources) global.headResources = { stylesheets: [], styles: [] }
  return global.headResources
}

async function processHeadElement(el: Element): Promise<void> {
  if (el.tagName === 'SCRIPT') {
    await loadScript(el as HTMLScriptElement, document.head)
    return
  }
  if (el.tagName === 'LINK' && el instanceof HTMLLinkElement && isStylesheetLink(el)) {
    const resolvedHref = resolveStylesheetUrl(el.getAttribute('href'))
    if (!resolvedHref) return
    const res = getHeadResources()
    if (!res.stylesheets.includes(resolvedHref)) res.stylesheets.push(resolvedHref)
    return
  }
  if (el.tagName === 'STYLE' && el instanceof HTMLStyleElement && el.textContent) {
    const res = getHeadResources()
    res.styles.push({ id: el.id || undefined, content: el.textContent })
  }
}

async function loadWidgetResourcesBody(communityUrl: string): Promise<void> {
  const response = await fetch(`${communityUrl}/widget/resourcesBody`, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget body resources: ${response.status}`)
  }

  const html = await response.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const scripts = Array.from(doc.querySelectorAll('script'))

  for (const script of scripts) {
    await loadScript(script)
  }
}

export async function ensureResourcesLoaded(communityUrl: string): Promise<void> {
  const global = getEmbedGlobal()

  if (global.loaded) return
  if (global.loading) return global.loading

  global.loading = (async () => {
    await loadWidgetResources(communityUrl)
    await loadWidgetResourcesBody(communityUrl)
    global.loaded = true
  })()

  return global.loading
}

export function isResourcesLoaded(): boolean {
  return getEmbedGlobal().loaded === true
}
